import { apiSlice } from './api';
import { ResponseBody } from '../types/common';

import {
  InitialValues,
  ProjectList,
  ProjectParam,
  SummaryDuration,
  ClassAttendances,
} from '../types/analysis';

export const analysisApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSummary: builder.query({
      query: (data?: SummaryDuration | any) => ({
        url: 'library/analysis/summary',
        params: data,
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    deleteAllActivity: builder.mutation<ResponseBody, void>({
      query: () => ({
        url: 'library/analysis/summary',
        method: 'DELETE',
      }),
    }),
    getProjectList: builder.query<ProjectList, ProjectParam>({
      query: (data) => ({
        url: 'library/analysis/projects',
        params: data,
      }),
      transformResponse: (res: ResponseBody) => res.data.projectList,
    }),
    getProject: builder.query({
      query: ({ projectId, seq }) => ({
        url: `library/analysis/projects/${projectId}`,
        params: seq,
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getAnnotation: builder.query({
      query: ({ projectId, seq }) => ({
        url: `library/analysis/projects/${projectId}/annotation`,
        params: { seq },
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getContentsActivity: builder.query({
      query: ({ projectId, seq }) => ({
        url: `library/analysis/projects/${projectId}/content`,
        params: { seq },
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getQuizActivity: builder.query({
      query: ({ projectId }) => ({
        url: `library/analysis/projects/${projectId}/quiz`,
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getQuizDetails: builder.query({
      query: ({ projectId, page, size, seq }) => ({
        url: `library/analysis/projects/${projectId}/quiz/detail`,
        params: { page, size, seq },
      }),
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page > 0) {
          currentCache.quizList = [
            ...currentCache.quizList,
            ...newItems.quizList,
          ];
        } else {
          return newItems;
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      transformResponse: (res: ResponseBody) => res.data,
    }),
    deleteProjectActivity: builder.mutation<ResponseBody, void>({
      query: (projectId) => ({
        url: `library/analysis/projects/${projectId}`,
        method: 'DELETE',
      }),
    }),
    getClassInit: builder.query<InitialValues, void>({
      query: () => ({
        url: 'library/analysis/class/init',
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getClassSummary: builder.mutation({
      query: (data) => ({
        url: 'library/analysis/class',
        method: 'POST',
        body: { data },
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getClassStudent: builder.query({
      query: ({ userId, duration }) => ({
        url: `library/analysis/class/${userId}`,
        params: duration,
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getClassProjectList: builder.query({
      query: (data) => ({
        url: 'library/analysis/class/projects',
        params: data,
      }),
      transformResponse: (res: ResponseBody) => res.data.projectList,
    }),
    getClassProject: builder.query({
      query: ({ projectId, userId, seq }) => ({
        url: `library/analysis/class/projects/${projectId}`,
        params: { userId, seq },
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getClassAnnotation: builder.query({
      query: ({ projectId, userId, seq }) => ({
        url: `library/analysis/class/projects/${projectId}/annotation`,
        params: { userId, seq },
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getClassContentsActivity: builder.query({
      query: ({ projectId, userId, seq }) => ({
        url: `library/analysis/class/projects/${projectId}/content`,
        params: { userId, seq },
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getClassQuizActivity: builder.query({
      query: ({ projectId, userId }) => ({
        url: `library/analysis/class/projects/${projectId}/quiz`,
        params: { userId },
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getClassQuizDetails: builder.query({
      query: ({ projectId, page, size, seq, userId }) => ({
        url: `library/analysis/class/projects/${projectId}/quiz/detail`,
        params: { page, size, seq, userId },
      }),
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page > 0) {
          currentCache.quizList = [
            ...currentCache.quizList,
            ...newItems.quizList,
          ];
        } else {
          return newItems;
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getStudentSummary: builder.mutation({
      query: (data) => ({
        url: 'library/analysis/student',
        method: 'POST',
        body: { data },
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getStudentProject: builder.query({
      query: ({ projectId, data }) => ({
        url: `library/analysis/student/projects/${projectId}`,
        method: 'POST',
        body: { data },
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getClassAttendances: builder.query<
      ClassAttendances,
      { seq?: number; attendanceDate: string }
    >({
      query: ({ seq, attendanceDate }) => ({
        url: 'library/analysis/class/attendances',
        params: { seq, attendanceDate },
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
  }),
});

export const {
  useGetSummaryQuery,
  useDeleteAllActivityMutation,
  useGetProjectListQuery,
  useGetProjectQuery,
  useGetAnnotationQuery,
  useGetContentsActivityQuery,
  useGetQuizActivityQuery,
  useGetQuizDetailsQuery,
  useDeleteProjectActivityMutation,
  useGetClassInitQuery,
  useGetClassSummaryMutation,
  useGetClassStudentQuery,
  useGetClassProjectQuery,
  useGetClassAnnotationQuery,
  useGetClassContentsActivityQuery,
  useGetClassQuizActivityQuery,
  useGetClassQuizDetailsQuery,
  useGetStudentSummaryMutation,
  useGetStudentProjectQuery,
  useGetClassAttendancesQuery,
} = analysisApiSlice;
